import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle, Loader2, Settings, List, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { WEBHOOK_URL_KEY } from './useDirectWebhookStatus';
import { getWebhookInfo, setupWebhook, testConnection } from '@/services/directTelegram';
import { getBotToken } from '@/services/directTelegram/token';
import { setupDefaultCommands, getBotCommands } from '@/services/directTelegram/commands';
import { generateWebhookUrl, clearWebhookCache, isWebhookUrlValid } from '@/services/telegram/webhookUtils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { isSystemBotTokenConfigured } from '@/services/telegram/botTokenService';

interface WebhookInfo {
  url: string;
  is_active: boolean;
  pending_update_count?: number;
  last_error_date?: number;
  last_error_message?: string;
}

export function WebhookSetupManager() {
  const [webhookInfo, setWebhookInfo] = useState<WebhookInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [customUrl, setCustomUrl] = useState<string>('');
  const [isSettingCommands, setIsSettingCommands] = useState(false);
  const [commands, setCommands] = useState<any[]>([]);
  const { toast } = useToast();

  // Generate the correct webhook URL dynamically
  const defaultWebhookUrl = generateWebhookUrl();

  useEffect(() => {
    // Clear any old cached URLs and use the dynamic URL
    const savedUrl = localStorage.getItem(WEBHOOK_URL_KEY);
    
    // If saved URL doesn't match current project, clear it
    if (savedUrl && !isWebhookUrlValid(savedUrl)) {
      console.log('Clearing outdated webhook URL cache');
      clearWebhookCache();
      setCustomUrl(defaultWebhookUrl);
    } else if (savedUrl) {
      setCustomUrl(savedUrl);
    } else {
      setCustomUrl(defaultWebhookUrl);
    }
    
    // Check bot commands
    fetchBotCommands();
  }, [defaultWebhookUrl]);

  const fetchBotCommands = async () => {
    const botCommands = await getBotCommands();
    if (botCommands) {
      setCommands(botCommands);
    }
  };

  const handleResetWebhook = async () => {
    setIsResetting(true);
    try {
      // Clear all cached data
      clearWebhookCache();
      
      // Reset to default URL
      setCustomUrl(defaultWebhookUrl);
      
      // Clear webhook info
      setWebhookInfo(null);
      
      toast({
        title: "Webhook Reset",
        description: "Webhook cache cleared. Please set up the webhook again.",
      });
      
      console.log('Webhook reset completed, using URL:', defaultWebhookUrl);
    } catch (error: any) {
      console.error('Failed to reset webhook:', error);
      toast({
        title: "Reset Failed",
        description: error.message || "Failed to reset webhook",
        variant: "destructive"
      });
    } finally {
      setIsResetting(false);
    }
  };

  const setupCommandsHandler = async () => {
    setIsSettingCommands(true);
    try {
      const success = await setupDefaultCommands();
      
      if (success) {
        toast({
          title: "Commands setup successful",
          description: "Default bot commands have been configured",
        });
        
        // Refresh commands
        await fetchBotCommands();
      } else {
        toast({
          title: "Commands setup failed",
          description: "Failed to set up bot commands",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Failed to set up commands:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSettingCommands(false);
    }
  };

  const checkWebhookStatus = async () => {
    setIsLoading(true);
    try {
      const isConfigured = await isSystemBotTokenConfigured();
      if (!isConfigured) {
        toast({
          title: "Bot token required",
          description: "Please set your bot token first",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      const info = await getWebhookInfo();
      setWebhookInfo(info);
      
      if (info && info.url) {
        // Check if the current webhook URL is valid for this project
        if (!isWebhookUrlValid(info.url)) {
          toast({
            title: "Outdated webhook detected",
            description: "The webhook is pointing to an old project. Please reset and reconfigure.",
            variant: "destructive"
          });
        }
        localStorage.setItem(WEBHOOK_URL_KEY, info.url);
        setCustomUrl(info.url);
      }
    } catch (error: any) {
      console.error('Error checking webhook status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to check webhook status",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebhookHandler = async () => {
    setIsSettingUp(true);
    try {
      const isConfigured = await isSystemBotTokenConfigured();
      if (!isConfigured) {
        toast({
          title: "Bot token required",
          description: "Please set your bot token first",
          variant: "destructive"
        });
        setIsSettingUp(false);
        return;
      }

      const urlToUse = customUrl || defaultWebhookUrl;

      // Save URL to localStorage
      localStorage.setItem(WEBHOOK_URL_KEY, urlToUse);

      const result = await setupWebhook(urlToUse);

      if (result.success) {
        toast({
          title: "Webhook setup successful",
          description: `Webhook set to: ${urlToUse}`,
        });
        
        // Refresh webhook status
        await checkWebhookStatus();
      } else {
        toast({
          title: "Webhook setup failed",
          description: result.error || "Failed to set up webhook",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Failed to set up webhook:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  const getStatusAlert = () => {
    if (!webhookInfo) return null;

    const isCurrentProject = isWebhookUrlValid(webhookInfo.url);

    if (webhookInfo.is_active && isCurrentProject) {
      return (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Webhook Active</AlertTitle>
          <AlertDescription>
            Your Telegram webhook is properly configured and active for this project.
            {webhookInfo.pending_update_count && webhookInfo.pending_update_count > 0 && (
              <div className="mt-2">
                Pending updates: {webhookInfo.pending_update_count}
              </div>
            )}
          </AlertDescription>
        </Alert>
      );
    }

    if (webhookInfo.is_active && !isCurrentProject) {
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Webhook Points to Wrong Project</AlertTitle>
          <AlertDescription>
            Your webhook is active but points to a different Supabase project. 
            Please reset and reconfigure the webhook for this project.
            <div className="mt-2 text-sm">
              Current: {webhookInfo.url}
              <br />
              Expected: {defaultWebhookUrl}
            </div>
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Webhook Not Active</AlertTitle>
        <AlertDescription>
          Your Telegram webhook is not set up. Configure a webhook URL and click "Setup Webhook".
          {webhookInfo.last_error_message && (
            <div className="mt-2 text-sm">
              Last error: {webhookInfo.last_error_message}
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  };

  useEffect(() => {
    checkWebhookStatus();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Webhook Setup
        </CardTitle>
        <CardDescription>
          Configure the Telegram webhook for your bot to receive messages
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {getStatusAlert()}
        
        {webhookInfo && (
          <div className="space-y-2">
            <div className="text-sm">
              <strong>Webhook URL:</strong> {webhookInfo.url || 'Not set'}
            </div>
            <div className="text-sm">
              <strong>Status:</strong> {webhookInfo.is_active ? 'Active' : 'Inactive'}
            </div>
            {webhookInfo.url && !isWebhookUrlValid(webhookInfo.url) && (
              <div className="text-sm text-orange-600">
                <strong>Warning:</strong> Webhook points to different project
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook-url">Webhook URL</Label>
            <Input
              id="webhook-url"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder={defaultWebhookUrl}
            />
            <p className="text-sm text-muted-foreground">
              Current project: {defaultWebhookUrl}
            </p>
          </div>
        </div>
        
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={checkWebhookStatus} 
              variant="outline" 
              disabled={isLoading || isSettingUp || isResetting}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                'Check Status'
              )}
            </Button>
            
            <Button 
              onClick={setupWebhookHandler} 
              disabled={isLoading || isSettingUp || isResetting}
            >
              {isSettingUp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Up...
                </>
              ) : (
                'Setup Webhook'
              )}
            </Button>

            <Button 
              onClick={handleResetWebhook} 
              variant="outline"
              disabled={isLoading || isSettingUp || isResetting}
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Webhook
                </>
              )}
            </Button>
          </div>
          
          <div className="pt-4 border-t">
            <h3 className="text-lg font-medium">Bot Commands</h3>
            
            {commands.length > 0 ? (
              <div className="mt-2 space-y-1">
                {commands.map((cmd, index) => (
                  <div key={index} className="text-sm">
                    <code className="bg-muted px-2 py-1 rounded">/{cmd.command}</code> - {cmd.description}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">No commands configured yet.</p>
            )}
            
            <Button 
              onClick={setupCommandsHandler} 
              className="mt-4"
              disabled={isSettingCommands}
              variant="outline"
              size="sm"
            >
              {isSettingCommands ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Up...
                </>
              ) : (
                <>
                  <List className="h-4 w-4 mr-2" />
                  Setup Default Commands
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
